import React from "react";
import { Box, Chip, InputAdornment, Paper, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { CodeBlock, Expect, Hint, Step, useSteps } from "../guide/GuideParts";
import { LinkCards, PageHeader, Screenshot, Section } from "../guide/DocParts";

// Azure App Services and Function Apps: what runs there, how to restart them, read their logs and renew certificates

const DASHBOARD =
  "https://portal.azure.com/#@genesiscloud.onmicrosoft.com/dashboard/arm/subscriptions/1e24f37d-1083-48ea-827b-0e5dea4461cb/resourcegroups/dashboards/providers/microsoft.portal/dashboards/2cd446d0-5ac4-40d5-bce0-2b5aeff5305f";

type Group = "Gateway" | "API" | "Web app" | "Function";

interface AppService {
  name: string;
  group: Group;
  text: string;
  function?: boolean;
}

// App Services of subscription webapp1 (resource list of the screenshot plus the ones named in the other pages)
const APPS: AppService[] = [
  { name: "gsc-r1-pr-ase-centralgateway-1", group: "Gateway", text: "CentralGateway – entry point for our own apps" },
  { name: "gsc-r1-pr-ase-externalcentralgateway-1", group: "Gateway", text: "ExternalGateway – entry point for partners" },
  { name: "gsc-r1-pr-ase-newsapi-1", group: "API", text: "News of the managers for the Handelsapp" },
  { name: "gsc-r1-pr-ase-masterdataapi-1", group: "API", text: "Master data, recycling footprint" },
  { name: "gsc-r1-pr-ase-materialdataapi-1", group: "API", text: "CellData values (currencies, metals)" },
  { name: "gsc-r1-pr-ase-abfuhrkalender-1", group: "Web app", text: "Abfuhrkalender (production)" },
  { name: "gsc-r1-de-ase-abfuhrkalender-1", group: "Web app", text: "Abfuhrkalender (development)" },
  { name: "gsc-r1-pr-ase-admindashboard-1", group: "Web app", text: "Admin dashboard" },
  { name: "gsc-r1-pr-ase-businesscards-1", group: "Web app", text: "Digital business cards" },
  { name: "gsc-r1-pr-ase-landingsite-1", group: "Web app", text: "Landing site" },
  { name: "gsc-r1-pr-ase-materialdataui-1", group: "Web app", text: "Material Data web app" },
  { name: "gsc-r1-pr-ase-mdm-1", group: "Web app", text: "MDM – master data management" },
  { name: "gsc-r1-pr-ase-pdm-1", group: "Web app", text: "PDM" },
  { name: "gsc-r1-pr-ase-pim-1", group: "Web app", text: "PIM" },
  { name: "gsc-r1-pr-ase-sandbox-1", group: "Web app", text: "This Sandbox portal" },
  { name: "gsc-r1-pr-ase-toolbox-2", group: "Web app", text: "Toolbox Digido" },
  { name: "gsc-r1-pr-ase-scadaplantat-2", group: "Web app", text: "SCADA plant (Janitza data points)" },
  { name: "gsc-r1-toursmonitor-at", group: "Web app", text: "Tours SMS monitor" },
  { name: "ImportMaterialDataFunctions", group: "Function", text: "CellData import (currencies, metals)", function: true },
  { name: "ImportMasterDataFunctions…", group: "Function", text: "Master data import", function: true },
  { name: "SMSDataFunctions", group: "Function", text: "Sends the tours SMS", function: true },
];

const GROUP_COLORS: Record<Group, string> = { Gateway: "#005f98", API: "#00897b", "Web app": "#0277bd", Function: "#e07b00" };

type Action = "restart" | "logs" | "status";

const cli = (app: AppService, action: Action) => {
  const kind = app.function ? "functionapp" : "webapp";
  const name = app.name.replace("…", "*");
  const id = `$id = az ${kind} list --query "[?name=='${name}'].id" -o tsv`;
  switch (action) {
    case "restart":
      return `${id}\naz ${kind} restart --ids $id`;
    case "logs":
      return `${id}\naz webapp log tail --ids $id`;
    case "status":
      return `az ${kind} list --query "[?name=='${name}'].{name:name, state:state, host:defaultHostName}" -o table`;
  }
};

const AppList: React.FunctionComponent<{ selected: string; onSelect: (name: string) => void }> = ({ selected, onSelect }) => {
  const [q, setQ] = React.useState("");
  const [group, setGroup] = React.useState<Group | "all">("all");
  const list = APPS.filter((a) => (group === "all" || a.group === group) && `${a.name} ${a.text}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Search app …"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <ToggleButtonGroup size="small" exclusive value={group} onChange={(_, v) => v && setGroup(v)}>
          <ToggleButton value="all">All</ToggleButton>
          {(Object.keys(GROUP_COLORS) as Group[]).map((g) => (
            <ToggleButton key={g} value={g}>
              {g}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 1.25 }}>
        {list.map((a) => (
          <Paper
            key={a.name}
            variant="outlined"
            onClick={() => onSelect(a.name)}
            sx={{
              p: 1.25,
              borderRadius: 2,
              cursor: "pointer",
              borderLeft: `4px solid ${GROUP_COLORS[a.group]}`,
              outline: selected === a.name ? `2px solid ${GROUP_COLORS[a.group]}` : "none",
              "&:hover": { boxShadow: 3 },
            }}
          >
            <Typography sx={{ fontFamily: "Consolas, monospace", fontSize: 12.5, fontWeight: 700, wordBreak: "break-all" }}>{a.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {a.text}
            </Typography>
            <Chip size="small" label={a.group} sx={{ mt: 0.5, height: 20, fontSize: 11, color: "#fff", background: GROUP_COLORS[a.group] }} />
          </Paper>
        ))}
      </Box>
    </>
  );
};

const Operate: React.FunctionComponent<{ app: AppService }> = ({ app }) => {
  const [action, setAction] = React.useState<Action>("restart");
  const { isDone, toggle } = useSteps(`${app.name}|${action}`);
  return (
    <>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 1 }}>
        <Typography sx={{ fontFamily: "Consolas, monospace", fontWeight: 700 }}>{app.name}</Typography>
        <ToggleButtonGroup size="small" exclusive value={action} onChange={(_, v) => v && setAction(v)}>
          <ToggleButton value="restart">Restart</ToggleButton>
          <ToggleButton value="logs">Live logs</ToggleButton>
          <ToggleButton value="status">Status</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Step n={1} title="In the Azure Portal" where="azure" done={isDone(1)} onToggle={toggle(1)}>
        {action === "restart" && (
          <>
            <p>
              Search <b>{app.name}</b> at the top of the portal → <b>Overview</b> → <b>Restart</b> → Yes. The app is unavailable for about a minute.
            </p>
            <Screenshot src="/ApplicationServices.PNG" caption="App Services → app → Overview: Stop / Restart" />
          </>
        )}
        {action === "logs" && (
          <p>
            Open <b>{app.name}</b> → Monitoring → <b>Log stream</b>. New lines appear live; for older errors use Application Insights → Failures.
          </p>
        )}
        {action === "status" && (
          <p>
            Open <b>{app.name}</b> → Overview: Status must be <b>Running</b>. “Diagnose and solve problems” shows availability and errors of the last 24 hours.
          </p>
        )}
      </Step>
      <Step n={2} title="Or with the Azure CLI" where="pc" done={isDone(2)} onToggle={toggle(2)}>
        <p>
          Needs <code>az login</code> once (PowerShell, subscription webapp1).
        </p>
        <CodeBlock label="PowerShell" code={cli(app, action)} />
        {action === "restart" && <Expect>no output and exit code 0. Open the app after a minute.</Expect>}
      </Step>
    </>
  );
};

const CertificateGuide: React.FunctionComponent = () => {
  const { isDone, toggle, count } = useSteps("cert");
  return (
    <>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
        <Chip label="*.genesiscloud.cc, genesiscloud.cc" color="primary" variant="outlined" />
        <Chip label="*.loacker.cc, loacker.cc" color="primary" variant="outlined" />
      </Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Most apps run under our own domains, so they use our <b>wildcard certificates</b> (.pfx with private key). A new certificate goes to the Azure Key Vault first and is then
        updated on every App Service that uses it. {count} of 6 steps done.
      </Typography>
      <Step n={1} title="Find out when the certificates expire" where="pc" done={isDone(1)} onToggle={toggle(1)}>
        <CodeBlock
          label="PowerShell"
          code={`az resource list --resource-type Microsoft.Web/certificates --query "[].id" -o tsv | ForEach-Object {
  az resource show --ids $_ --query "{host:properties.subjectName, expires:properties.expirationDate, app:name}" -o tsv
}`}
        />
        <Expect>one line per certificate and App Service with the expiry date. Start the renewal at least 30 days before.</Expect>
      </Step>
      <Step n={2} title="Get the new certificate" where="browser" done={isDone(2)} onToggle={toggle(2)}>
        <p>From the certificate provider, as <b>.pfx with private key</b> and its password (keep both in the vault, never in a mail or chat).</p>
      </Step>
      <Step n={3} title="Upload it to the Key Vault" where="azure" done={isDone(3)} onToggle={toggle(3)}>
        <p>
          Key Vault → <b>Certificates</b> → <b>Generate/Import</b> → Method <b>Import</b> → choose the .pfx, enter the password. Use the same certificate name as before with a new
          version, so the apps can pick it up.
        </p>
      </Step>
      <Step n={4} title="Update each App Service" where="azure" done={isDone(4)} onToggle={toggle(4)}>
        <p>
          App Service → <b>TLS/SSL settings</b> (Certificates) → <b>Private Key Certificates (.pfx)</b> → <b>Import Key Vault Certificate</b> (or Upload Certificate).
        </p>
        <Screenshot src="/certificates.PNG" caption="TLS/SSL settings → Private Key Certificates: the two wildcard certificates" />
      </Step>
      <Step n={5} title="Switch the bindings" where="azure" done={isDone(5)} onToggle={toggle(5)}>
        <p>
          <b>Custom domains</b> (or Bindings) → each domain → <b>Add binding</b> → the new certificate, type <b>SNI SSL</b>. Then remove the old certificate from the App Service.
        </p>
      </Step>
      <Step n={6} title="Test" where="pc" done={isDone(6)} onToggle={toggle(6)}>
        <CodeBlock code={`curl.exe -vI https://sandboxde.genesiscloud.cc 2>&1 | findstr /i "expire subject"`} />
        <Expect>the new expiry date. Check in the browser too (padlock → certificate).</Expect>
      </Step>
    </>
  );
};

export const AzureServicesPage: React.FunctionComponent = () => {
  const [selected, setSelected] = React.useState(APPS[0].name);
  const app = APPS.find((a) => a.name === selected)!;
  return (
    <Paper sx={{ p: 2, width: "100%" }}>
      <PageHeader
        title="Azure App Services"
        subtitle="Our web apps, APIs and functions on Microsoft Azure (subscription webapp1, West Europe)"
        stats={[
          { label: "App Services", value: APPS.filter((a) => !a.function).length },
          { label: "Function Apps", value: APPS.filter((a) => a.function).length },
          { label: "Wildcard certs", value: 2 },
        ]}
      />
      <LinkCards
        links={[
          { title: "Azure dashboard", text: "Our overview dashboard", url: DASHBOARD, color: "#0078d4" },
          { title: "All App Services", url: "https://portal.azure.com/#browse/Microsoft.Web%2Fsites", color: "#0078d4" },
          { title: "Azure status", text: "Is Azure itself down?", url: "https://azure.status.microsoft/status", color: "#43a047" },
        ]}
      />

      <Section title="What runs on Azure" text="Click an app to restart it or read its logs">
        <AppList selected={selected} onSelect={setSelected} />
      </Section>

      <Section title="Restart · logs · status">
        <Hint>When an app does not respond: first check its status and live logs, then restart. A restart fixes hangs but not configuration errors – those show up again in the logs.</Hint>
        <Operate app={app} />
      </Section>

      <Section title="Renew the website certificates">
        <CertificateGuide />
      </Section>
    </Paper>
  );
};
